import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { Grid } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Delete,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { dropTipoEmitente, dropTipoTransportador } from 'utils/drops';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { useDialog } from 'components/Modals';
import Card from 'components/Card';
import Button from 'components/Button';
import VirtualDrop from 'components/VirtualDrop';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import moment from 'moment';

const MDFE = ({
  onSubmit,
  onDelete,
  drops = {},
  documento = {},
  gridProps = {},
  loading = false,
  disableActions = false,
}) => {
  const { openDialog } = useDialog();

  const DocumentoModal = () => {
    const defaultValues = {
      documento_mdfe_id: documento?.id,
      documento_id: null,
    };
    const { control, handleSubmit } = useForm({ defaultValues });
    const { postLoading } = useContext(DocumentosContext);

    const onConfirm = (values) => {
      const data = {
        MDFe: { documento_id: documento?.id, Documentos: [values] },
      };
      onSubmit(data);
    };

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <VirtualDrop
            name="documento_id"
            control={control}
            label="Documento"
            options={drops?.Documento?.filter(
              (f) => f?.status === 'CONCLUIDO' && f?.modelo === 'NFe'
            )}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            variant="contained"
            loading={postLoading}
            onClick={handleSubmit(onConfirm)}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    );
  };

  const PercursoModal = ({ percursos = [] }) => {
    const defaultValues = {
      documento_mdfe_id: documento?.id,
      sequencia: percursos?.length + 1,
      motorista_id: null,
      ufdest_id: null,
      ufdesc_id: null,
      saida: null,
      chegada: null,
      kmsaida: '',
      kmchegada: '',
    };
    const { control, handleSubmit } = useForm({ defaultValues });
    const { postLoading } = useContext(DocumentosContext);

    const onConfirm = (values) => {
      const data = {
        MDFe: { documento_id: documento?.id, Percursos: [values] },
      };
      onSubmit(data);
    };

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Dropdown
            name="motorista_id"
            control={control}
            label="Motorista"
            options={drops?.EntidadeTipo?.filter(
              (t) => t?.tipo === 'MOTORISTA'
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <VirtualDrop
            name="ufdest_id"
            control={control}
            label="UF de Destino"
            options={drops?.UF}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <VirtualDrop
            name="ufdesc_id"
            control={control}
            label="UF de Descarregamento"
            options={drops?.UF}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            name="saida"
            control={control}
            label="Data de Saída"
            type="datetime"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            name="chegada"
            control={control}
            label="Data de Chegada"
            type="datetime"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask name="kmsaida" control={control} label="KM de Saída" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask name="kmchegada" control={control} label="KM de Chegada" />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            variant="outlined"
            loading={postLoading}
            onClick={handleSubmit(onConfirm)}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    );
  };

  const Mdfe = () => {
    const defaultValues = {
      documento_id: documento?.id,
      tpemit: documento?.DocumentoMdfe?.tpemit || null,
      tptransp: documento?.DocumentoMdfe?.tptransp || null,
      frota_id: documento?.DocumentoMdfe?.frota_id || null,
      reboque1_id: documento?.DocumentoMdfe?.reboque1_id || null,
      reboque2_id: documento?.DocumentoMdfe?.reboque2_id || null,
    };
    const { control, handleSubmit } = useForm({ defaultValues });
    return (
      <Card title="MDFE">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Dropdown
              name="tpemit"
              control={control}
              label="Tipo de Emitente"
              options={dropTipoEmitente}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Dropdown
              name="tptransp"
              control={control}
              label="Tipo de Transportador"
              options={dropTipoTransportador}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Dropdown
              name="frota_id"
              control={control}
              label="Frota"
              options={drops?.Frota}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Dropdown
              name="reboque1_id"
              control={control}
              label="Reboque 1"
              options={drops?.Frota}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Dropdown
              name="reboque2_id"
              control={control}
              label="Reboque 2"
              options={drops?.Frota}
            />
          </Grid>
          <Grid item xs={12} textAlign="center">
            {!disableActions && (
              <Button
                color="primary"
                variant="contained"
                loading={loading}
                onClick={handleSubmit((MDFe) => onSubmit({ MDFe }))}
              >
                Salvar
              </Button>
            )}
          </Grid>
        </Grid>
      </Card>
    );
  };

  const Documentos = () => {
    const rotina = 'DocumentoMdfeDocumento';
    const documentos =
      documento?.DocumentoMdfe?.DocumentoMdfeDocumentos?.map((d) => ({
        id: d?.id,
        dtemissao: d?.Documento?.dtemissao,
        documento: d?.Documento?.documento,
        cliente: d?.Documento?.Entidade?.nome,
        cidade: d?.Documento?.cidade,
        uf: d?.Documento?.uf,
        pesoliq: d?.Documento?.DocumentoTransportes?.find(
          (f) => f?.tipotransp === 'DESPACHO'
        )?.pesoliq,
        pesobrt: d?.Documento?.DocumentoTransportes?.find(
          (f) => f?.tipotransp === 'DESPACHO'
        )?.pesobrt,
      })) || [];
    return (
      <Card title="Documentos">
        <Grid container spacing={2}>
          {!disableActions && (
            <Grid item xs={12} textAlign="center">
              <Button
                color="primary"
                variant="outlined"
                loading={loading}
                onClick={() =>
                  openDialog(<DocumentoModal />, 'Adicionar Documento')
                }
              >
                Adicionar
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <DataGrid
              rows={documentos}
              columns={[
                {
                  field: 'actions',
                  type: 'actions',
                  headerName: 'Ações',
                  hide: disableActions,
                  width: 100,
                  getActions: ({ id }) => [
                    <GridActionsCellItem
                      icon={<Delete />}
                      label="Excluir"
                      onClick={() => onDelete({ rotina, id })}
                    />,
                  ],
                },
                {
                  field: 'dtemissao',
                  headerName: 'Data de Emissão',
                  sortable: false,
                  width: 200,
                  valueFormatter: ({ value }) =>
                    value && moment(value).format('DD/MM/YYYY'),
                },
                {
                  field: 'documento',
                  headerName: 'Documento',
                  width: 200,
                  sortable: false,
                },
                {
                  field: 'cliente',
                  headerName: 'Cliente',
                  width: 600,
                  sortable: false,
                },
                {
                  field: 'cidade',
                  headerName: 'Cidade',
                  width: 300,
                  valueGetter: ({ row }) => {
                    let str = '';
                    const cidade = row?.cidade || '';
                    const uf = row?.uf || '';
                    str += cidade;
                    if (Boolean(cidade) && Boolean(uf)) {
                      str += ' - ';
                    }
                    str += uf;
                    return str;
                  },
                  sortable: false,
                },
                {
                  field: 'pesoliq',
                  headerName: 'Peso Líquido (KG)',
                  type: 'number',
                  width: 200,
                  sortable: false,
                },
                {
                  field: 'pesobrt',
                  headerName: 'Peso Bruto (KG)',
                  type: 'number',
                  width: 200,
                  sortable: false,
                },
              ]}
              {...gridProps}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const Percurso = () => {
    const rotina = 'DocumentoMdfePercurso';
    const percursos = documento?.DocumentoMdfe?.DocumentoMdfePercursos || [];

    const onMove = (row, way) => {
      const from = percursos?.findIndex((p) => p?.id === row?.id);
      const to = way === 'up' ? from - 1 : from + 1;
      const cond =
        (way === 'up' && from > 0) ||
        (way === 'down' && from < percursos?.length - 1);
      if (cond) {
        const nextState = [...percursos];
        nextState?.splice(from, 1);
        nextState?.splice(to, 0, row);
        const data = {
          MDFe: {
            documento_id: documento?.id,
            Percursos: nextState?.map((n, i) => ({ ...n, sequencia: i + 1 })),
          },
        };
        onSubmit(data);
      }
    };

    return (
      <Card title="Percurso">
        <Grid container spacing={2}>
          {!disableActions && (
            <Grid item xs={12} textAlign="center">
              <Button
                color="primary"
                variant="outlined"
                loading={loading}
                onClick={() =>
                  openDialog(
                    <PercursoModal percursos={percursos} />,
                    'Adicionar Percurso'
                  )
                }
              >
                Adicionar
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <DataGrid
              rows={percursos}
              columns={[
                {
                  field: 'actions',
                  type: 'actions',
                  width: 150,
                  headerName: 'Ações',
                  hide: disableActions,
                  getActions: ({ id, row }) => [
                    <GridActionsCellItem
                      icon={<KeyboardArrowUp />}
                      label="Mover para cima"
                      disabled={row?.sequencia === 1}
                      onClick={() => onMove(row, 'up')}
                    />,
                    <GridActionsCellItem
                      icon={<KeyboardArrowDown />}
                      label="Mover para baixo"
                      disabled={row?.sequencia === percursos?.length}
                      onClick={() => onMove(row, 'down')}
                    />,
                    <GridActionsCellItem
                      icon={<Delete />}
                      label="Excluir"
                      onClick={() => onDelete({ rotina, id })}
                    />,
                  ],
                },
                {
                  field: 'sequencia',
                  headerName: 'Sequência',
                  width: 100,
                  sortable: false,
                },
                {
                  field: 'destino',
                  headerName: 'Destino',
                  width: 300,
                  sortable: false,
                  valueGetter: ({ row }) => {
                    let str = '';
                    const cidade = row?.Cidade?.localidade || '';
                    const uf = row?.Uf?.sigla || '';
                    str += cidade;
                    if (Boolean(cidade) && Boolean(uf)) {
                      str += ' - ';
                    }
                    str += uf;
                    return str;
                  },
                },
                {
                  field: 'saida',
                  headerName: 'Saída',
                  width: 300,
                  sortable: false,
                  valueGetter: ({ row }) => {
                    let str = '';
                    const saida = row?.saida;
                    const km_saida = row?.kmsaida;
                    if (Boolean(saida)) {
                      str += moment(saida).format('DD/MM/YY [às] HH:mm');
                    }
                    if (Boolean(saida) && Boolean(km_saida)) {
                      str += ' (';
                    }
                    if (Boolean(km_saida)) {
                      str += 'KM ' + km_saida;
                    }
                    if (Boolean(saida) && Boolean(km_saida)) {
                      str += ')';
                    }
                    return str;
                  },
                },
                {
                  field: 'chegada',
                  headerName: 'Chegada',
                  width: 300,
                  sortable: false,
                  valueGetter: ({ row }) => {
                    let str = '';
                    const chegada = row?.chegada;
                    const km_chegada = row?.kmchegada;
                    if (Boolean(chegada)) {
                      str += moment(chegada).format('DD/MM/YY [às] HH:mm');
                    }
                    if (Boolean(chegada) && Boolean(km_chegada)) {
                      str += ' (';
                    }
                    if (Boolean(km_chegada)) {
                      str += 'KM ' + km_chegada;
                    }
                    if (Boolean(chegada) && Boolean(km_chegada)) {
                      str += ')';
                    }
                    return str;
                  },
                },
              ]}
              {...gridProps}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Mdfe />
      </Grid>
      <Grid item xs={12}>
        <Documentos />
      </Grid>
      <Grid item xs={12}>
        <Percurso />
      </Grid>
    </Grid>
  );
};

export default MDFE;
